import { useState } from 'react';

export default function ComplianceHero() {
  const [activeCard, setActiveCard] = useState(0);

  const valueCards = [
    {
      icon: 'ri-file-list-3-line',
      title: '统一口径',
      desc: '数据与资料"说得清"',
      detail: '建立统一的数据标准和资料规范，确保全链路信息一致性',
      color: 'from-teal-500 to-cyan-500',
    },
    {
      icon: 'ri-footprint-line',
      title: '过程留痕',
      desc: '每票业务"追得到"',
      detail: '全流程操作记录，关键节点自动存档，业务轨迹清晰可查',
      color: 'from-emerald-500 to-green-500',
    },
    {
      icon: 'ri-archive-line',
      title: '证据链归档',
      desc: '抽查核销"拿得出"',
      detail: '单证自动归集，证据链完整保存，随时应对审计核查',
      color: 'from-orange-400 to-amber-400',
    },
  ];

  return (
    <section className="relative min-h-screen flex items-center pt-[72px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://public.readdy.ai/ai/img_res/8d1f3844e80122dd16de1575ad5bf794.jpg"
          alt="合规与治理背景"
          className="w-full h-full object-cover object-top"
        />
        {/* 统一双层遮罩结构 */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/65 via-white/40 to-teal-50/50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/40 to-transparent"></div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* 统一顶部标签徽章样式 */}
            <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2">
              <i className="ri-shield-check-line text-teal-700"></i>
              <span className="text-sm font-medium text-teal-700">企业内部治理 · 全链路合规管理</span>
            </div>

            {/* 统一主标题样式 */}
            <h1 className="text-5xl lg:text-6xl font-extrabold leading-tight">
              <span className="bg-gradient-to-r from-[#0D9488] to-[#14B8A6] bg-clip-text text-transparent drop-shadow-sm">
                合规与治理
              </span>
              <br />
              <span className="text-gray-800">
                企业稳健发展基石
              </span>
            </h1>

            {/* 统一正文样式 */}
            <div className="space-y-3">
              <p className="text-lg text-gray-700 leading-relaxed">
                治理是合规的基础：口径统一、责任清晰、过程可追溯。
              </p>
              <p className="text-lg text-gray-500 leading-relaxed">
                合规反过来提升治理质量，保障企业稳健与高质量发展。
              </p>
            </div>

            {/* 统一按钮组样式 */}
            <div className="space-y-3 max-w-md pt-4">
              {/* 主按钮：橙色渐变 rounded-full h-14 shadow-lg hover:scale-105 */}
              <button
                onClick={() => {
                  const event = new CustomEvent('openComplianceDrawer');
                  window.dispatchEvent(event);
                }}
                className="w-full h-14 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-medium rounded-full hover:scale-105 transition-transform flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg"
              >
                咨询合规治理方案
                <i className="ri-arrow-right-line"></i>
              </button>

              {/* 次按钮：bg-white/70 backdrop-blur-sm border-2 border-[#14B8A6] rounded-full h-14 */}
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/capabilities')}
                className="w-full h-14 bg-white/70 backdrop-blur-sm border-2 border-[#14B8A6] text-[#0D9488] font-medium rounded-full hover:bg-[#14B8A6] hover:text-white hover:border-[#14B8A6] transition-all whitespace-nowrap cursor-pointer"
              >
                查看产品及价格
              </button>

              {/* 文字链接按钮：text-[#0D9488] text-sm h-12 带右上角箭头图标 */}
              <button
                onClick={() => {
                  const section = document.getElementById('deliverables-section');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full h-12 text-[#0D9488] text-sm font-medium hover:underline transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-1"
              >
                查看交付物示例
                <i className="ri-arrow-right-up-line text-base"></i>
              </button>
            </div>

            {/* 统一底部合规边界说明：border-t border-gray-200 pt-6，橙色竖线 + text-xs text-gray-500 */}
            <div className="pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 flex items-start gap-2">
                <span className="w-1 h-4 bg-[#FF6B35] rounded-full flex-shrink-0 mt-0.5"></span>
                <span className="leading-relaxed">
                  <strong className="text-gray-600">治理价值：</strong>从"能操作"升级为"可解释、可追溯、可复盘"，建立企业合规管理体系。
                </span>
              </p>
            </div>
          </div>

          {/* Right Visual - Value Cards */}
          <div className="relative flex flex-col gap-6 justify-center">
            {valueCards.map((card, index) => (
              <div
                key={card.title}
                onMouseEnter={() => setActiveCard(index)}
                className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all duration-400 backdrop-blur-sm ${
                  activeCard === index
                    ? 'bg-white/90 border-teal-200 shadow-xl scale-[1.03]'
                    : 'bg-white/80 border-teal-100 shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-br ${card.color} shadow-md flex-shrink-0`}>
                    <i className={`${card.icon} text-2xl text-white`}></i>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-xl font-bold mb-1 transition-colors duration-300 ${
                      activeCard === index ? 'text-[#0D9488]' : 'text-gray-800'
                    }`}>
                      {card.title}
                    </h3>
                    <p className={`text-sm mb-2 transition-colors duration-300 ${
                      activeCard === index ? 'text-[#0D9488]' : 'text-gray-600'
                    }`}>
                      {card.desc}
                    </p>
                    
                    {/* Expanded detail */}
                    {activeCard === index && (
                      <p className="text-sm text-gray-500 leading-relaxed mt-3 pt-3 border-t border-teal-100">
                        {card.detail}
                      </p>
                    )}
                  </div>

                  {/* Arrow */}
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0 ${
                    activeCard === index ? `bg-gradient-to-br ${card.color}` : 'bg-gray-100'
                  }`}>
                    <i className={`ri-arrow-right-line text-sm ${activeCard === index ? 'text-white' : 'text-gray-400'}`}></i>
                  </div>
                </div>

                {/* Active indicator */}
                {activeCard === index && (
                  <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-16 bg-gradient-to-b from-[#0D9488] to-[#14B8A6] rounded-full"></div>
                )}
              </div>
            ))}

            {/* Bottom hint */}
            <p className="text-center text-xs text-gray-400 mt-2">
              悬停卡片查看详细说明
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}